import dashboardLiveBreakpoints, { dashboardLiveBreakpointComputed } from "./dashboard/dashboardLiveBreakpoints";
import dashboardLiveIssueSummary, { dashboardLiveIssueSummaryComputed } from "./dashboard/dashboardLiveIssueSummary";

Vue.asyncComponent('cms-dashboard', {
    props: {
        previewWidgets: {
            type: Array,
            required: false,
            default: null,
        },
    },
    /**
     * Create the reactive state for this dashboard component.
     */
    data() {
        return {
            loading: false,
            widgets: [],
            tabs: [],
            breakpoints: [],
            dashboardContext: null,
            activeTabId: null,
            showTabsMenu: false,
            dashboardLayoutElements: [],
            viewportWidth: null,
            viewportHeight: null,
        }
    },
    computed: {
        /**
         * Check whether preview mode.
         */
        isPreviewMode() {
            return Array.isArray(this.previewWidgets);
        },
        /**
         * All dashboard widgets.
         */
        allDashboardWidgets() {
            if (this.isPreviewMode) {
                return this.previewWidgets;
            }

            return this.widgets;
        },
        /**
         * Dashboard widgets.
         */
        dashboardWidgets() {
            return this.getWidgetsForTab(this.activeDashboardTabId);
        },
        /**
         * Check whether the active tab uses the checks accordion layout.
         */
        useCheckAccordionLayout() {
            return this.dashboardWidgets.some((widget) => widget?.type === 'cms-dashboard-checks-accordion');
        },
        /**
         * Default tab id.
         */
        defaultTabId() {
            if (!this.tabs.length || !this.tabs[0].id) {
                return 'default';
            }

            return this.tabs[0].id;
        },
        /**
         * Active dashboard tab id.
         */
        activeDashboardTabId() {
            return this.activeTabId || this.defaultTabId;
        },
        /**
         * Check whether compact dashboard tabs.
         */
        isCompactDashboardTabs() {
            return Number(this.viewportWidth || 0) > 0 && Number(this.viewportWidth) <= 767;
        },
        /**
         * Active dashboard tab title.
         */
        activeDashboardTabTitle() {
            const activeTab = this.tabs.find((tab) => tab.id === this.activeDashboardTabId);

            if (activeTab?.title) {
                return activeTab.title;
            }

            return this.$t('Tabs');
        },
        /**
         * Show tabs.
         */
        showTabs() {
            return this.tabs.length > 1;
        },
        /**
         * Show workspace switch.
         */
        showWorkspaceSwitch() {
            const workspace = this.getWorkspace();

            return !this.isPreviewMode && workspace && workspace.id === '';
        },
        /**
         * Workspace logo.
         */
        workspaceLogo() {
            const workspace = this.getWorkspace();

            if (workspace && workspace.logo) {
                return workspace.logo;
            }

            return this.$getSetting('assets.logo');
        },
        /**
         * Workspace title.
         */
        workspaceTitle() {
            const workspace = this.getWorkspace();

            if (workspace && workspace.title) {
                return workspace.title;
            }

            return null;
        },
        /**
         * Base dashboard uri.
         */
        baseDashboardUri() {
            return this.$getSetting('paths.baseCms') + '/dashboard';
        },
        /**
         * Dashboard grid columns.
         */
        dashboardGridColumns() {
            return 12;
        },
        /**
         * Dashboard grid rows.
         */
        dashboardGridRows() {
            return this.getDashboardGridRowCount(this.dashboardWidgets);
        },
        /**
         * Dashboard grid style.
         */
        dashboardGridStyle() {
            const useScrollableRows = Number(this.viewportWidth || 0) > 991
                && Number(this.viewportHeight || 0) > 0
                && Number(this.viewportHeight || 0) < 968;

            return {
                '--dashboard-grid-rows': this.dashboardGridRows,
                gridTemplateColumns: `repeat(${this.dashboardGridColumns}, minmax(0, 1fr))`,
                gridTemplateRows: useScrollableRows
                    ? `repeat(${this.dashboardGridRows}, minmax(var(--dashboard-grid-row-min-height), auto))`
                    : `repeat(${this.dashboardGridRows}, minmax(0, 1fr))`,
            };
        },
        /**
         * Resolve the translated critical alert title.
         */
        resolvedCriticalAlertTitle() {
            return this.$t('Belangrijke meldingen');
        },
        /**
         * Resolve the current dashboard owner type.
         */
        dashboardOwnerType() {
            return this.dashboardContext?.ownerType || 'default';
        },
        /**
         * Check whether to show the dashboard scope indicator.
         */
        showDashboardScopeIndicator() {
            return !this.isPreviewMode;
        },
        /**
         * Resolve the icon for the current dashboard scope.
         */
        dashboardScopeIcon() {
            const iconByOwnerType = {
                default: 'public',
                role: 'admin_panel_settings',
                user: 'person',
            };

            return iconByOwnerType[this.dashboardOwnerType] || iconByOwnerType.default;
        },
        /**
         * Resolve the label for the current dashboard scope.
         */
        dashboardScopeLabel() {
            const labelByOwnerType = {
                default: this.$t('Algemeen dashboard'),
                role: this.$t('Roldashboard'),
                user: this.$t('Gebruikersdashboard'),
            };

            return labelByOwnerType[this.dashboardOwnerType] || labelByOwnerType.default;
        },
        ...dashboardLiveBreakpointComputed,
        ...dashboardLiveIssueSummaryComputed,
    },
    methods: {
        /**
         * Get normalized render attributes for one dashboard widget.
         *
         * @param {*} widget
         */
        getWidgetAttributes(widget) {
            if (!widget || typeof widget !== 'object') {
                return {};
            }

            if (!widget.attributes || typeof widget.attributes !== 'object') {
                return {};
            }

            return widget.attributes;
        },
        /*
         * Dashboard widgets can belong to different tabs. Keep the fallback logic
         * in one place so filtering and summaries stay consistent.
         */
        getWidgetTabId(widget) {
            const attributes = this.getWidgetAttributes(widget);

            if (attributes.tabId) {
                return attributes.tabId;
            }

            return this.defaultTabId;
        },
        /**
         * Get dashboard widgets that belong to one tab.
         *
         * @param {*} tabId
         */
        getWidgetsForTab(tabId) {
            const widgets = [];

            for (const widget of this.allDashboardWidgets) {
                if (this.getWidgetTabId(widget) !== tabId) {
                    continue;
                }

                widgets.push(widget);
            }

            return widgets;
        },
        handleCheckWidgetInput(widgetId, value) {
            this.handleWidget(value, this.contextualPageJumperWidgets, widgetId);
        },
        /*
         * Several computed properties depend on the active workspace payload.
         * Resolve it once here to keep those computed properties simple.
         */
        getWorkspace() {
            const userInterface = this.$userInterfaceHandler.getUserInterface();

            if (!userInterface || !userInterface.workspace) {
                return null;
            }

            return userInterface.workspace;
        },
        /**
         * Enable the page-level dashboard layout classes.
         */
        enableDashboardPageLayout() {
            if (this.isPreviewMode || typeof document === 'undefined') {
                return;
            }

            document.body.classList.add('cms-dashboard-page-active');
            document.documentElement.classList.add('cms-dashboard-page-active');

            this.dashboardLayoutElements = [
                this.$el?.closest('.ak-wrapper'),
                this.$el?.closest('.ak-content'),
            ].filter(Boolean);

            this.dashboardLayoutElements.forEach((element) => {
                element.classList.add('cms-dashboard-page-active');
            });
        },
        /**
         * Remove the page-level dashboard layout classes.
         */
        disableDashboardPageLayout() {
            if (typeof document !== 'undefined') {
                document.body.classList.remove('cms-dashboard-page-active');
                document.documentElement.classList.remove('cms-dashboard-page-active');
            }

            this.dashboardLayoutElements.forEach((element) => {
                element.classList.remove('cms-dashboard-page-active');
            });

            this.dashboardLayoutElements = [];
        },
        /**
         * Fetch live dashboard data from the backend.
         */
        async fetchDashboardData() {
            this.loading = true;

            try {
                const result = await this.$get(this.baseDashboardUri);
                this.dashboardContext = result.dashboardContext || null;
                this.widgets = result.widgets;
                this.tabs = this.normalizeTabs(result.tabs);
                this.breakpoints = this.normalizeRuntimeBreakpoints(result.breakpoints);
                this.ensureActiveTab();
            } finally {
                this.loading = false;
            }
        },
        /**
         * Update viewport width.
         */
        updateViewportWidth() {
            if (typeof window === 'undefined') {
                return;
            }

            this.viewportWidth = window.innerWidth;
            this.viewportHeight = window.innerHeight;
        },
        /**
         * Normalize tabs.
         *
         * @param {*} tabs
         */
        normalizeTabs(tabs) {
            const normalizedTabs = [];
            const sourceTabs = Array.isArray(tabs) ? tabs : [];

            for (let tabIndex = 0; tabIndex < sourceTabs.length; tabIndex += 1) {
                const tab = sourceTabs[tabIndex];
                const normalizedTab = {
                    id: tab?.id || `tab-${tabIndex + 1}`,
                    title: tab?.title || `${this.$t('Tab')} ${tabIndex + 1}`,
                };

                if (!normalizedTab.id) {
                    continue;
                }

                normalizedTabs.push(normalizedTab);
            }

            if (normalizedTabs.length) {
                return normalizedTabs;
            }

            return [{
                id: 'default',
                title: `${this.$t('Tab')} 1`,
            }];
        },
        /**
         * Ensure the active dashboard tab still exists.
         */
        ensureActiveTab() {
            if (!this.tabs.find((tab) => tab.id === this.activeTabId)) {
                this.activeTabId = this.defaultTabId;
            }
        },
        /**
         * Get image like widget alignment style.
         *
         * @param {*} widget
         */
        getImageLikeWidgetAlignmentStyle(widget) {
            if (! ['cms-active-workspace-logo', 'ak-image'].includes(widget?.type)) {
                return {};
            }

            const attributes = this.getWidgetAttributes(widget);
            const justifyContentMap = {
                left: 'flex-start',
                center: 'center',
                right: 'flex-end',
            };
            const alignItemsMap = {
                top: 'flex-start',
                center: 'center',
                bottom: 'flex-end',
            };

            return {
                alignItems: alignItemsMap[attributes.verticalAlignment] || 'center',
                display: 'flex',
                justifyContent: justifyContentMap[attributes.horizontalAlignment] || 'center',
            };
        },
        /**
         * Get the CSS grid style for one dashboard widget.
         *
         * @param {*} widget
         */
        getWidgetGridStyle(widget) {
            const layout = this.getWidgetLayout(widget);
            const isTitleWidget = widget?.type === 'cms-dashboard-title';

            return {
                ...this.getImageLikeWidgetAlignmentStyle(widget),
                background: isTitleWidget ? 'transparent' : null,
                border: isTitleWidget ? '0' : null,
                borderRadius: isTitleWidget ? '0' : null,
                boxShadow: isTitleWidget ? 'none' : null,
                gridColumn: `${layout.column} / span ${layout.columnSpan}`,
                gridRow: `${layout.row} / span ${layout.rowSpan}`,
                display: layout.hidden
                    ? 'none'
                    : (['cms-active-workspace-logo', 'ak-image'].includes(widget?.type) ? 'flex' : null),
            };
        },
        /**
         * Get render attributes for one dashboard widget.
         *
         * @param {*} widget
         */
        getWidgetRenderAttributes(widget) {
            const attributes = {
                ...this.getWidgetAttributes(widget),
            };

            /*
             * The summary widget receives its items at render time because the list
             * depends on the current state of the other widgets on the page.
             */
            if (widget?.type === 'cms-dashboard-issues-summary') {
                attributes.items = this.getIssueSummaryItems();
            }

            return {
                ...attributes,
                id: widget.id,
            };
        },
        ...dashboardLiveBreakpoints,
        ...dashboardLiveIssueSummary,
    },
    /**
     * Initialize this dashboard component after it is created.
     */
    created() {
        this.tabs = this.normalizeTabs([]);
        this.$root.$on('cms-dashboard-refresh', this.fetchDashboardData);

        if (! this.showWorkspaceSwitch && ! this.isPreviewMode) {
            this.fetchDashboardData();
        }
    },
    /**
     * Attach dashboard behavior after the component is mounted.
     */
    mounted() {
        this.updateViewportWidth();
        window.addEventListener('resize', this.updateViewportWidth);
        this.enableDashboardPageLayout();
    },
    /**
     * Clean up dashboard behavior before the component is destroyed.
     */
    beforeDestroy() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.updateViewportWidth);
        }

        this.$root.$off('cms-dashboard-refresh', this.fetchDashboardData);
        this.disableDashboardPageLayout();
    }
}, 'cms/dashboard/live/cms-dashboard.html');
